import React, { useEffect, useState } from 'react';
import { AlertTriangle, Grid3X3, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/api';
import { formatPeriod } from './RoeLeanOverview';

const CELL = { green: 'bg-emerald-500/20 text-emerald-200 ring-emerald-500/30', amber: 'bg-amber-500/20 text-amber-200 ring-amber-500/30', red: 'bg-rose-500/25 text-rose-200 ring-rose-500/40' };
const CONSTRUCTS = ['confidence', 'application', 'behavior'];

// Cohort × capability grid — where do skills stall between Confidence and Application?
export default function CapabilityHeatmap({ organizationId, period, onPick }) {
  const [data, setData] = useState(null);
  const [construct, setConstruct] = useState('application');
  useEffect(() => {
    const p = new URLSearchParams();
    if (organizationId && organizationId !== '_all') p.set('organization_id', organizationId);
    if (period) p.set('period', period);
    setData(null);
    api.get(`/transfer-health/heatmap?${p}`).then((r) => setData(r.data)).catch(() => setData({ has_data: false }));
  }, [organizationId, period]);

  if (!data) return <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-500" /></div>;
  if (!data.has_data) return <p className="text-xs text-slate-500" data-testid="th-heatmap-empty">No capability-tagged evidence yet — add a <code>capability</code> column to your import.</p>;
  return (
    <div className="rounded-xl bg-[#0b1220] ring-1 ring-[#1f2a44] p-3 space-y-2" data-testid="th-heatmap">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 inline-flex items-center gap-1.5"><Grid3X3 className="h-3 w-3" /> Cohort × capability · {formatPeriod(data.period)}</p>
        <div className="flex gap-1" data-testid="th-heatmap-construct">
          {CONSTRUCTS.map((c) => <button key={c} type="button" onClick={() => setConstruct(c)} className={cn('px-2 py-0.5 rounded-md text-[10px] uppercase tracking-wide border', construct === c ? 'border-teal-500/50 text-teal-300 bg-teal-500/10' : 'border-[#22304d] text-slate-500 hover:text-slate-300')} data-testid={`th-heatmap-${c}`}>{c}</button>)}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs min-w-full">
          <thead><tr><th className="text-left text-[10px] uppercase tracking-wide text-slate-500 py-1 pr-3">Cohort</th>{data.capabilities.map((c) => <th key={c} className="text-[10px] uppercase tracking-wide text-slate-500 px-1 py-1 font-medium">{c}</th>)}</tr></thead>
          <tbody>
            {data.cohorts.map((co) => (
              <tr key={co.id}>
                <td className="py-1 pr-3 text-slate-300 whitespace-nowrap">{co.name}</td>
                {data.capabilities.map((cap) => {
                  const cell = data.cells[co.id]?.[cap];
                  if (!cell) return <td key={cap} className="px-1 py-1 text-center text-slate-700">·</td>;
                  const v = cell[construct];
                  return (
                    <td key={cap} className="px-1 py-1">
                      <button type="button" onClick={() => onPick?.(cap)} title={`${co.name} · ${cap}: Confidence ${Math.round(cell.confidence ?? 0)} · Application ${cell.application == null ? '—' : Math.round(cell.application)} · Behavior ${Math.round(cell.behavior ?? 0)} · Conf→App ${cell.conf_app_ratio ?? '—'}% · n=${cell.n}`}
                        className={cn('w-full rounded-md ring-1 px-2 py-1.5 text-center font-semibold tabular-nums relative', CELL[cell.bands[construct]] || 'bg-white/5 text-slate-500 ring-white/10')} data-testid={`th-cell-${co.id}-${cap}`}>
                        {v == null ? '—' : Math.round(v)}
                        {cell.stall && <AlertTriangle className="h-3 w-3 text-rose-300 absolute top-0.5 right-0.5" data-testid="th-cell-stall" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-slate-500" data-testid="th-heatmap-stalls">
        {data.stalls.length ? `${data.stalls.length} stall${data.stalls.length === 1 ? '' : 's'} (confident but <85% converts to application): ${data.stalls.slice(0, 3).map((s) => `${s.cohort} · ${s.capability} (${Math.round(s.ratio)}%)`).join('; ')}` : 'No stalls — every capability with healthy confidence is converting into application.'}
      </p>
    </div>
  );
}
